import { CSSProperties, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

// project-imports
import MainCard from 'components/MainCard';
import { ThemeDirection } from 'config';
import navigation from 'menu-items';

// third-party
import { FormattedMessage } from 'react-intl';

// assets
import { ArrowRight2, Buildings2, Home3 } from 'iconsax-reactjs';

// types
import { NavItemType } from 'types/menu';
import { OverrideIcon } from 'types/root';

interface BreadcrumbLinkProps {
  title: string;
  to?: string;
  icon?: string | OverrideIcon;
}

interface BreadCrumbSxProps extends CSSProperties {
  mb?: string | number;
  bgcolor?: string;
}

interface Props {
  card?: boolean;
  custom?: boolean;
  divider?: boolean;
  heading?: string;
  icon?: boolean;
  icons?: boolean;
  links?: BreadcrumbLinkProps[];
  maxItems?: number;
  rightAlign?: boolean;
  separator?: OverrideIcon;
  title?: boolean;
  titleBottom?: boolean;
  sx?: BreadCrumbSxProps;
}

// ==============================|| BREADCRUMBS - HELPERS ||============================== //

function resolveDemoPath(pathname: string): string {
  return pathname.includes('/components-overview/breadcrumbs') ? '/apps/customer/customer-card' : pathname;
}

function pathMatchesItemUrl(itemUrl: string, pathname: string): boolean {
  if (pathname === itemUrl) return true;
  return itemUrl.length > 1 && (pathname.startsWith(`${itemUrl}/`) || pathname.startsWith(`${itemUrl}?`));
}

function scanCollapses(
  parent: NavItemType,
  pathname: string,
  custom: boolean,
  setPair: (main: NavItemType, item: NavItemType) => void
): void {
  if (custom || !parent.children) return;

  parent.children.forEach((collapse: NavItemType) => {
    if (collapse.type === 'collapse') {
      scanCollapses(collapse, pathname, custom, setPair);
      if (collapse.url === pathname) {
        setPair(collapse, collapse);
      }
    } else if (collapse.type === 'item') {
      const u = typeof collapse.url === 'string' ? collapse.url : '';
      if (pathMatchesItemUrl(u, pathname)) {
        setPair(parent, collapse);
      }
    }
  });
}

function scanNavigationForPath(
  items: NavItemType[] | undefined,
  pathname: string,
  custom: boolean,
  setPair: (main: NavItemType, item: NavItemType) => void
): void {
  items?.forEach((menu: NavItemType) => {
    if (menu.type !== 'group') return;
    if (menu.url && menu.url === pathname) {
      setPair(menu, menu);
      return;
    }
    scanCollapses(menu, pathname, custom, setPair);
  });
}

function breadcrumbCardSx(card: boolean, sx?: BreadCrumbSxProps) {
  return card === false
    ? { mb: 3, bgcolor: 'transparent', borderRadius: 0, overflow: 'visible', boxShadow: 'none', ...sx }
    : { mb: 3, ...sx };
}

type IconSx = Record<string, unknown>;

function BreadcrumbHomeLink({
  icon,
  icons,
  iconSX,
  variant = 'body1',
  color = 'text.primary',
}: Readonly<{
  icon?: boolean;
  icons?: boolean;
  iconSX: IconSx;
  variant?: 'body1' | 'h6';
  color?: string;
}>) {
  return (
    <Typography
      component={Link}
      to="/"
      variant={variant}
      sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
      color={color}
    >
      {Boolean(icons) && <Home3 style={iconSX as React.CSSProperties} />}
      {Boolean(icon) && !icons && <Home3 variant="Bold" style={{ ...iconSX, marginRight: 0 } as React.CSSProperties} />}
      {(!icon || icons) && <FormattedMessage id="home" />}
    </Typography>
  );
}

function BreadcrumbCardShell({
  card,
  sx,
  others,
  rightAlign,
  showHeadingBefore,
  showHeadingAfter,
  heading,
  custom,
  itemTitleId,
  divider,
  children,
}: Readonly<{
  card: boolean;
  sx?: BreadCrumbSxProps;
  others: Record<string, unknown>;
  rightAlign?: boolean;
  showHeadingBefore: boolean;
  showHeadingAfter: boolean;
  heading?: string;
  custom: boolean;
  itemTitleId?: NavItemType['title'];
  divider: boolean;
  children: ReactElement;
}>) {
  const titleId = custom ? heading : itemTitleId;

  return (
    <MainCard
      border={card}
      sx={breadcrumbCardSx(card, sx)}
      {...others}
      content={card}
      boxShadow={false}
    >
      <Grid
        container
        direction={rightAlign ? 'row' : 'column'}
        justifyContent={rightAlign ? 'space-between' : 'flex-start'}
        alignItems={rightAlign ? 'center' : 'flex-start'}
        spacing={0.5}
      >
        {showHeadingBefore && titleId ? (
          <Grid>
            <Typography variant="h2" sx={{ fontWeight: 700 }}>
              <FormattedMessage id={titleId} />
            </Typography>
          </Grid>
        ) : null}
        <Grid>{children}</Grid>
        {showHeadingAfter && titleId ? (
          <Grid sx={{ mt: card === false ? 0 : 1 }}>
            <Typography variant="h2" sx={{ fontWeight: 700 }}>
              <FormattedMessage id={titleId} />
            </Typography>
          </Grid>
        ) : null}
      </Grid>
      {card === false && divider ? <Divider sx={{ mt: 2 }} /> : null}
    </MainCard>
  );
}

function LinksBreadcrumbList({
  links,
  maxItems,
  separatorIcon,
  iconSX,
}: Readonly<{
  links: BreadcrumbLinkProps[];
  maxItems: number;
  separatorIcon: ReactElement;
  iconSX: IconSx;
}>) {
  return (
    <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems} separator={separatorIcon}>
      {links.map((link: BreadcrumbLinkProps) => {
        const LinkIcon = (link.icon ?? Buildings2) as OverrideIcon;
        return (
          <Typography
            key={`${link.title}::${link.to ?? ''}`}
            {...(link.to && { component: Link, to: link.to })}
            variant="body1"
            sx={{ textDecoration: 'none', fontWeight: 500, ...(link.to && { fontWeight: 400, cursor: 'pointer' }) }}
            color={link.to ? 'text.secondary' : 'text.primary'}
          >
            {Boolean(link.icon) && <LinkIcon style={iconSX as React.CSSProperties} />}
            <FormattedMessage id={link.title} />
          </Typography>
        );
      })}
    </MuiBreadcrumbs>
  );
}

type BreadcrumbUiParams = {
  main?: NavItemType;
  item?: NavItemType;
  card: boolean;
  custom: boolean;
  divider: boolean;
  heading?: string;
  icon?: boolean;
  icons?: boolean;
  links?: BreadcrumbLinkProps[];
  maxBreadcrumbItems: number;
  rightAlign?: boolean;
  separatorIcon: ReactElement;
  title?: boolean;
  titleBottom?: boolean;
  sx?: BreadCrumbSxProps;
  others: Record<string, unknown>;
  iconSX: IconSx;
};

function buildCollapseMainTypography(main: NavItemType, icons?: boolean, iconSX?: IconSx): ReactElement {
  const CollapseIcon = (main.icon ?? Buildings2) as OverrideIcon;
  return (
    <Typography
      {...(main.url ? { component: Link, to: main.url } : {})}
      variant="body1"
      sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
      color={window.location.pathname === main.url ? 'text.primary' : 'text.secondary'}
    >
      {Boolean(icons) && <CollapseIcon style={(iconSX ?? {}) as React.CSSProperties} />}
      <FormattedMessage id={main.title} />
    </Typography>
  );
}

function renderCollapseCardIfNeeded(p: BreadcrumbUiParams, mainContent: ReactElement): ReactElement | null {
  const show = p.main?.type === 'collapse' && !p.main?.breadcrumbs && p.custom;
  if (!show) return null;
  return (
    <BreadcrumbCardShell
      card={p.card}
      sx={p.sx}
      others={p.others}
      rightAlign={p.rightAlign}
      showHeadingBefore={false}
      showHeadingAfter={Boolean(p.title && p.titleBottom)}
      heading={p.heading}
      custom={p.custom}
      itemTitleId={p.main?.title}
      divider={p.divider !== false}
    >
      <MuiBreadcrumbs aria-label="breadcrumb" maxItems={p.maxBreadcrumbItems} separator={p.separatorIcon}>
        <BreadcrumbHomeLink icon={p.icon} icons={p.icons} iconSX={p.iconSX} />
        {mainContent}
      </MuiBreadcrumbs>
    </BreadcrumbCardShell>
  );
}

function renderItemCardIfNeeded(p: BreadcrumbUiParams, mainContent: ReactElement | undefined): ReactElement | null {
  const showBlock =
    p.item?.type === 'item' || (p.item?.type === 'group' && Boolean(p.item.url)) || p.custom;
  if (!showBlock) return null;

  const itemTitle = p.item?.title ?? '';
  const ItemIcon = (p.item?.icon ?? Buildings2) as OverrideIcon;
  const itemContent = (
    <Typography variant="body1" color="text.primary" sx={{ display: 'flex', fontWeight: 500, alignItems: 'center' }}>
      {Boolean(p.icons) && <ItemIcon style={p.iconSX as React.CSSProperties} />}
      <FormattedMessage id={itemTitle} />
    </Typography>
  );

  let innerCrumbs: ReactElement = (
    <MuiBreadcrumbs aria-label="breadcrumb" maxItems={p.maxBreadcrumbItems} separator={p.separatorIcon}>
      <BreadcrumbHomeLink icon={p.icon} icons={p.icons} iconSX={p.iconSX} variant="h6" color="text.secondary" />
      {mainContent}
      {itemContent}
    </MuiBreadcrumbs>
  );

  if (p.custom && p.links && p.links.length > 0) {
    innerCrumbs = (
      <LinksBreadcrumbList links={p.links} maxItems={p.maxBreadcrumbItems} separatorIcon={p.separatorIcon} iconSX={p.iconSX} />
    );
  }

  const showCard = p.item?.breadcrumbs !== false || p.custom;
  if (!showCard) return null;

  return (
    <BreadcrumbCardShell
      card={p.card}
      sx={p.sx}
      others={p.others}
      rightAlign={p.rightAlign}
      showHeadingBefore={Boolean(p.title && !p.titleBottom)}
      showHeadingAfter={Boolean(p.title && p.titleBottom)}
      heading={p.heading}
      custom={p.custom}
      itemTitleId={p.item?.title}
      divider={p.divider !== false}
    >
      {innerCrumbs}
    </BreadcrumbCardShell>
  );
}

function buildBreadcrumbUi(p: BreadcrumbUiParams): ReactElement {
  let mainContent: ReactElement | undefined;
  if (p.main?.type === 'collapse' && !p.main?.breadcrumbs) {
    mainContent = buildCollapseMainTypography(p.main, p.icons, p.iconSX);
  }

  if (mainContent) {
    const collapseCard = renderCollapseCardIfNeeded(p, mainContent);
    if (collapseCard) return collapseCard;
  }

  return renderItemCardIfNeeded(p, mainContent) ?? <Typography />;
}

// ==============================|| BREADCRUMBS ||============================== //

export default function Breadcrumbs({
  card = false,
  custom = false,
  divider = false,
  heading,
  icon,
  icons,
  links,
  maxItems,
  rightAlign,
  separator,
  title = true,
  titleBottom = true,
  sx,
  ...others
}: Readonly<Props>) {
  const theme = useTheme();
  const location = useLocation();
  const [main, setMain] = useState<NavItemType | undefined>();
  const [item, setItem] = useState<NavItemType>();

  const iconSX: IconSx = useMemo(
    () => ({
      marginRight: theme.direction === ThemeDirection.RTL ? 0 : theme.spacing(0.75),
      marginLeft: theme.direction === ThemeDirection.RTL ? theme.spacing(0.75) : 0,
      width: '1rem',
      height: '1rem',
      color: theme.palette.secondary.main,
    }),
    [theme]
  );

  const customLocation = useMemo(() => resolveDemoPath(location.pathname), [location.pathname]);

  const applyNavMatch = useCallback((nextMain: NavItemType, nextItem: NavItemType) => {
    setMain(nextMain);
    setItem(nextItem);
  }, []);

  useEffect(() => {
    scanNavigationForPath(navigation?.items, customLocation, custom, applyNavMatch);
  }, [customLocation, custom, applyNavMatch]);

  const maxBreadcrumbItems = maxItems ?? 8;

  const separatorIcon = useMemo(() => {
    if (!separator) return <ArrowRight2 size={12} />;
    const SeparatorIcon = separator;
    return <SeparatorIcon size={12} />;
  }, [separator]);

  return useMemo(
    () =>
      buildBreadcrumbUi({
        main,
        item,
        card,
        custom,
        divider,
        heading,
        icon,
        icons,
        links,
        maxBreadcrumbItems,
        rightAlign,
        separatorIcon,
        title,
        titleBottom,
        sx,
        others: others as Record<string, unknown>,
        iconSX,
      }),
    [
      main,
      item,
      card,
      custom,
      divider,
      heading,
      icon,
      icons,
      links,
      maxBreadcrumbItems,
      rightAlign,
      separatorIcon,
      title,
      titleBottom,
      sx,
      others,
      iconSX,
    ]
  );
}
